/* ITT-SUNSHINE-DROPLETS (2026-10-29): golden droplets of sunshine falling
 * gently across the whole store. One fixed canvas overlay, vanilla JS,
 * no dependencies, no external requests, no images — droplets are a single
 * pre-rendered radial-gradient sprite drawn with drawImage.
 * Removal: delete this asset + its one include line in layout/theme.liquid.
 *
 * Behaviour contract:
 * - pointer-events:none; z-index 3 — above base page content, below the
 *   theme's heightened layer (4), sticky header (8), overlays (16) and
 *   menu/drawers (18); native <dialog> drawers/modals paint in the top
 *   layer, always above this canvas.
 * - DPR-aware, capped at 2. requestAnimationFrame loop; pauses on
 *   visibilitychange (hidden tab = zero cost).
 * - Adaptive quality: FPS is measured on a rolling window, first judged
 *   after a 4s warm-up (page-load jank is not punished). Sustained <50 FPS
 *   halves the droplet count; sustained <35 FPS fades the layer out and
 *   stops the loop entirely.
 * - prefers-reduced-motion: the layer stays OFF (no canvas is created).
 */
(function () {
  'use strict';
  if (window.__ittSunshineLoaded) return;
  window.__ittSunshineLoaded = true;

  var api = { state: 'init', count: 0, fps: 0 };
  window.__ittSunshine = api;

  if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    api.state = 'off-reduced-motion';
    return;
  }

  var Z_INDEX = 3;
  var DPR_CAP = 2;
  var WARMUP_MS = 4000;
  var EVAL_MS = 2500;

  var canvas = document.createElement('canvas');
  canvas.id = 'itt-sunshine-droplets';
  canvas.setAttribute('aria-hidden', 'true');
  canvas.style.cssText =
    'position:fixed;inset:0;width:100%;height:100%;pointer-events:none;z-index:' +
    Z_INDEX + ';opacity:1;transition:opacity 1.2s ease;';

  var ctx = canvas.getContext('2d');
  if (!ctx) { api.state = 'off-no-context'; return; }

  /* --- one glow sprite: warm gold rim -> pale gold -> near-white core --- */
  var SPRITE = 128;
  var sprite = document.createElement('canvas');
  sprite.width = SPRITE; sprite.height = SPRITE;
  var sctx = sprite.getContext('2d');
  var grad = sctx.createRadialGradient(SPRITE / 2, SPRITE / 2, 0, SPRITE / 2, SPRITE / 2, SPRITE / 2);
  grad.addColorStop(0.0, 'rgba(255,250,220,1)');
  grad.addColorStop(0.22, 'rgba(255,233,160,0.9)');
  grad.addColorStop(0.52, 'rgba(255,194,75,0.38)');
  grad.addColorStop(1.0, 'rgba(255,194,75,0)');
  sctx.fillStyle = grad;
  sctx.fillRect(0, 0, SPRITE, SPRITE);

  var W = 0, H = 0, DPR = 1;
  var droplets = [];
  var baseCount = 0;
  var level = 0; /* 0 = full, 1 = halved */

  function targetBase() {
    var w = window.innerWidth || 1024;
    if (w >= 1024) return 34;
    if (w >= 768) return 26;
    return 18;
  }

  function makeDroplet(seedY) {
    return {
      x: Math.random(),
      y: seedY ? Math.random() * H : -30 - Math.random() * 60,
      size: 12 + Math.random() * 18,          /* glow diameter, CSS px */
      speed: 11 + Math.random() * 22,          /* px/s — slow, sunlit drift */
      swayAmp: 6 + Math.random() * 14,
      swayFreq: 0.25 + Math.random() * 0.5,
      phase: Math.random() * Math.PI * 2,
      alpha: 0.22 + Math.random() * 0.34,
      catchLight: Math.random() < 0.3,         /* a few catch the light */
      twinkleFreq: 0.6 + Math.random() * 1.1
    };
  }

  function syncCount() {
    var want = level === 0 ? baseCount : Math.max(6, Math.ceil(baseCount / 2));
    while (droplets.length < want) droplets.push(makeDroplet(true));
    if (droplets.length > want) droplets.length = want;
    api.count = droplets.length;
  }

  function resize() {
    DPR = Math.min(window.devicePixelRatio || 1, DPR_CAP);
    W = window.innerWidth; H = window.innerHeight;
    canvas.width = Math.round(W * DPR);
    canvas.height = Math.round(H * DPR);
    baseCount = targetBase();
    syncCount();
  }

  var rafId = 0;
  var running = false;
  var stopped = false;
  var startTime = 0;
  var lastTs = 0;
  var frames = 0;
  var fpsWindowStart = 0;
  var evalCount = 0;

  function fadeOutAndStop() {
    stopped = true;
    api.state = 'off-low-fps';
    canvas.style.opacity = '0';
    window.setTimeout(function () {
      stopLoop();
      if (canvas.parentNode) canvas.parentNode.removeChild(canvas);
    }, 1300);
  }

  function evaluate(now) {
    var elapsed = now - fpsWindowStart;
    if (elapsed < EVAL_MS) return;
    var fps = frames * 1000 / elapsed;
    api.fps = Math.round(fps * 10) / 10;
    frames = 0;
    fpsWindowStart = now;
    evalCount++;
    if (now - startTime < WARMUP_MS) return;
    if (fps < 35) { fadeOutAndStop(); return; }
    if (fps < 50 && level === 0) {
      level = 1;
      api.state = 'running-halved';
      syncCount();
    }
  }

  function frame(ts) {
    if (!running) return;
    rafId = requestAnimationFrame(frame);
    if (!lastTs) { lastTs = ts; fpsWindowStart = ts; frames = 0; return; }
    var dt = Math.min(0.05, (ts - lastTs) / 1000);
    lastTs = ts;
    frames++;
    var t = ts / 1000;

    ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
    ctx.clearRect(0, 0, W, H);
    for (var i = 0; i < droplets.length; i++) {
      var d = droplets[i];
      d.y += d.speed * dt;
      if (d.y - d.size > H) {
        droplets[i] = makeDroplet(false);
        continue;
      }
      var cx = d.x * W + Math.sin(t * d.swayFreq * Math.PI * 2 + d.phase) * d.swayAmp;
      var a = d.alpha;
      if (d.catchLight) {
        var tw = 0.5 + 0.5 * Math.sin(t * d.twinkleFreq * Math.PI * 2 + d.phase);
        a = d.alpha * (0.55 + 0.65 * tw);
      }
      ctx.globalAlpha = a;
      /* slight vertical stretch — a droplet, not a polka dot */
      ctx.drawImage(sprite, cx - d.size / 2, d.y - d.size * 0.58, d.size, d.size * 1.16);
    }
    ctx.globalAlpha = 1;
    evaluate(ts);
  }

  function startLoop() {
    if (running || stopped) return;
    running = true;
    lastTs = 0;
    rafId = requestAnimationFrame(frame);
  }
  function stopLoop() {
    running = false;
    if (rafId) cancelAnimationFrame(rafId);
    rafId = 0;
  }

  document.addEventListener('visibilitychange', function () {
    if (document.hidden) stopLoop();
    else if (!stopped) startLoop();
  });

  var resizeTimer = 0;
  window.addEventListener('resize', function () {
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(resize, 150);
  });

  function init() {
    document.body.appendChild(canvas);
    resize();
    startTime = performance.now();
    api.state = 'running';
    api.count = droplets.length;
    startLoop();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
